/**
 * One file for a run (a Q&A questionnaire, a Review draft): drag-and-drop or a
 * chooser, validated client-side with the collection-upload rules, then
 * uploaded at once with progress. The value is the server's upload reference;
 * its `upload_id` goes into the run's `upload_ids`.
 */

import { useRef, useState, type DragEvent } from "react";
import { ActionIcon, Group, Paper, Progress, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconCloudUpload, IconFileCheck, IconX } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  UPLOAD_EXTENSIONS,
  formatBytes,
  validateUpload,
} from "@/features/collections/collectionMeta";
import { ApiError } from "@/shared/api/apiFetch";
import { uploadForRun, type UploadRefOut } from "@/shared/api/solutionRuns";

interface Props {
  value: UploadRefOut | null;
  onChange: (value: UploadRefOut | null) => void;
  label?: string;
  disabled?: boolean;
}

export function SolutionUpload({ value, onChange, label = "File", disabled = false }: Props) {
  const getToken = useAccessToken();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const accept = async (file: File | undefined) => {
    if (!file || disabled) return;
    const invalid = validateUpload(file);
    if (invalid) {
      setProblem(`${file.name}: ${invalid}`);
      return;
    }
    setProblem(null);
    setProgress(0);
    try {
      const ref = await uploadForRun(await getToken(), file, (p) =>
        setProgress(Math.round(p * 100)),
      );
      onChange(ref);
    } catch (err) {
      setProblem(err instanceof ApiError ? err.message : String(err));
    } finally {
      setProgress(null);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    void accept(e.dataTransfer.files[0]);
  };

  if (value) {
    return (
      <Paper withBorder radius="md" p="sm" data-testid="solution-upload-value">
        <Group justify="space-between" wrap="nowrap">
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon variant="light" color="green">
              <IconFileCheck size={16} />
            </ThemeIcon>
            <div>
              <Text size="sm" fw={500} truncate>
                {value.filename}
              </Text>
              <Text size="xs" c="dimmed">
                {formatBytes(value.size_bytes)}
              </Text>
            </div>
          </Group>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Remove file"
            onClick={() => onChange(null)}
            disabled={disabled}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Paper>
    );
  }

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      <Paper
        withBorder
        radius="md"
        p="lg"
        data-testid="solution-upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          borderStyle: "dashed",
          background: dragging ? "var(--mantine-color-blue-light)" : undefined,
        }}
      >
        <Stack align="center" gap={4}>
          <IconCloudUpload size={28} />
          <Text size="sm">Drop a file here, or click to choose</Text>
          <Text size="xs" c="dimmed">
            PDF, Word, Excel, PowerPoint, CSV or text — up to 50 MB
          </Text>
        </Stack>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={UPLOAD_EXTENSIONS.join(",")}
          aria-label={label}
          onChange={(e) => {
            void accept(e.currentTarget.files?.[0]);
            e.currentTarget.value = "";
          }}
        />
      </Paper>
      {progress !== null && <Progress value={progress} aria-label="Upload progress" />}
      {problem && (
        <Text size="xs" c="red" role="alert">
          {problem}
        </Text>
      )}
    </Stack>
  );
}
